import DateDiff from './date-diff';

const name = 'Sort Helper';

const isValidDate = (prop: undefined | number | string) => {
    if (!prop || prop === '') return false;
    return new Date(prop).toString() !== 'Invalid Date';
};

const compareStrings = (a: unknown, b: unknown, ascending = true): number => {
    const first = a == null ? '' : String(a);
    const second = b == null ? '' : String(b);
    if (first === '' && second === '') return 0;
    if (first === '') return 1;
    if (second === '') return -1;
    const result = first.localeCompare(second, undefined, { sensitivity: 'base', numeric: true });
    return ascending ? result : -result;
};

const sortAsc = (items: AnonymousObject, sortProp1: string, sortProp2 = '') => {
    if (!items.length) return items;
    return items.sort((a: AnonymousObject, b: AnonymousObject) => {
        const primary = compareStrings(a[sortProp1], b[sortProp1]);
        if (primary !== 0) return primary;
        if (sortProp2 !== '') return compareStrings(a[sortProp2], b[sortProp2]);
        return 0;
    });
};

const sortQLAsc = (items: AnonymousObject, sortProp1: string, sortProp2 = '') => {
    if (!items.length) return items;
    return items.sort((a: AnonymousObject, b: AnonymousObject) => {
        const aVal = Array.isArray(a[sortProp1]) ? (a[sortProp1][0] || '') : (a[sortProp1] || '');
        const bVal = Array.isArray(b[sortProp1]) ? (b[sortProp1][0] || '') : (b[sortProp1] || '');
        const primary = compareStrings(aVal, bVal);
        if (primary !== 0) return primary;
        if (sortProp2 !== '') {
            const aVal2 = Array.isArray(a[sortProp2]) ? (a[sortProp2][0] || '') : (a[sortProp2] || '');
            const bVal2 = Array.isArray(b[sortProp2]) ? (b[sortProp2][0] || '') : (b[sortProp2] || '');
            return compareStrings(aVal2, bVal2);
        }
        return 0;
    });
};

const sortDesc = (items: AnonymousObject, sortProp1: string, sortProp2 = '') => {
    if (!items.length) return items;
    return items.sort((a: AnonymousObject, b: AnonymousObject) => {
        const primary = compareStrings(a[sortProp1], b[sortProp1], false);
        if (primary !== 0) return primary;
        if (sortProp2 !== '') return compareStrings(a[sortProp2], b[sortProp2], false);
        return 0;
    });
};

const sortQLDesc = (items: AnonymousObject, sortProp1: string, sortProp2 = '') => {
    if (!items.length) return items;
    return items.sort((a: AnonymousObject, b: AnonymousObject) => {
        const aVal = Array.isArray(a[sortProp1]) ? (a[sortProp1][0] || '') : (a[sortProp1] || '');
        const bVal = Array.isArray(b[sortProp1]) ? (b[sortProp1][0] || '') : (b[sortProp1] || '');
        const primary = compareStrings(aVal, bVal, false);
        if (primary !== 0) return primary;
        if (sortProp2 !== '') {
            const aVal2 = Array.isArray(a[sortProp2]) ? (a[sortProp2][0] || '') : (a[sortProp2] || '');
            const bVal2 = Array.isArray(b[sortProp2]) ? (b[sortProp2][0] || '') : (b[sortProp2] || '');
            return compareStrings(aVal2, bVal2, false);
        }
        return 0;
    });
};

const sortAscByDate = (items: AnonymousObject, sortProp1: string, sortProp2 = '') => {
    return items.sort((a: AnonymousObject, b: AnonymousObject) => {
        // a is null? last
        if (!isValidDate(a[sortProp1])) return 1;
        // b is null? last
        if (!isValidDate(b[sortProp1])) return -1;
        if (new Date(a[sortProp1]) < new Date(b[sortProp1])) return -1;
        if (new Date(a[sortProp1]) > new Date(b[sortProp1])) return 1;
        if (sortProp2 !== '') {
            return compareStrings(a[sortProp2], b[sortProp2]);
        }
        return 0;
    });
};

const sortDescByDate = (items: AnonymousObject, sortProp1: string, sortProp2 = '') => {
    return items.sort((a: AnonymousObject, b: AnonymousObject) => {
        // a is null? last
        if (!isValidDate(a[sortProp1])) return 1;
        // b is null? last
        if (!isValidDate(b[sortProp1])) return -1;
        if (new Date(a[sortProp1]) < new Date(b[sortProp1])) return 1;
        if (new Date(a[sortProp1]) > new Date(b[sortProp1])) return -1;
        if (sortProp2 !== '') {
            return compareStrings(a[sortProp2], b[sortProp2], false);
        }
        return 0;
    });
};

const sortByEnhanced = (items: AnonymousObject) => {
    const enhancedDate = items
        .filter((item: DataProfileCard) => {
            if (!item.enhancementDate) return false;
            return isValidDate(item.enhancementDate) && DateDiff(item.enhancementDate) > 0;
        }) // plus filter to remove future dates use DateDiff
        .sort((a: AnonymousObject, b: AnonymousObject) => {
            // a is null? last
            if (!isValidDate(a.enhancementDate)) return 1;
            // b is null? last
            if (!isValidDate(b.enhancementDate)) return -1;
            if (new Date(a.enhancementDate) < new Date(b.enhancementDate)) return 1;
            if (new Date(a.enhancementDate) > new Date(b.enhancementDate)) return -1;
            return 0;
        });

    const nonEnhancedDate = items
        .filter((item: DataProfileCard) => {
            if (!item.enhancementDate) return true;
            if (!isValidDate(item.enhancementDate)) return true;
            return DateDiff(item.enhancementDate) <= 0;
        })
        .sort((a: AnonymousObject, b: AnonymousObject) => {
            // a is null? last
            if (!isValidDate(a.addedDate)) return 1;
            // b is null? last
            if (!isValidDate(b.addedDate)) return -1;
            if (new Date(a.addedDate) < new Date(b.addedDate)) return 1;
            if (new Date(a.addedDate) > new Date(b.addedDate)) return -1;
            return 0;
        });

    return enhancedDate.concat(nonEnhancedDate);
};

export { name as default, sortAsc, sortDesc, sortAscByDate, sortDescByDate, sortByEnhanced, isValidDate, sortQLAsc, sortQLDesc };
